import { useRef, useState } from 'react';
import { ChevronDown, ChevronUp, ExternalLink, FileText, Loader2, Trophy } from 'lucide-react';
import { useWorkspace } from '@/shared/hooks/use-workspace';
import { Button } from '@/shared/components/shadcn/button';
import { Badge } from '@/shared/components/shadcn/badge';
import { cn } from '@/shared/utils';
import { PlanningBriefSheet } from '@/features/campaign-planning/components/planning-brief-sheet';
import type {
  AnalysisReport,
  InsightCard as InsightCardType,
  PostReference,
  VideoPost,
} from '../types';
import { InsightCard } from './insight-card';

function localizePost(post: VideoPost, isKo: boolean): VideoPost {
  if (isKo) return post;
  return {
    ...post,
    title: post.titleEn ?? post.title,
    description: post.descriptionEn ?? post.description,
    reasons: post.reasonsEn ?? post.reasons,
  };
}

interface Props {
  report: AnalysisReport;
  selectedCampaignNames?: string[];
  campaignName?: string;
}

function PostVideo({ post, className }: { post: PostReference; className?: string }) {
  const videoRef = useRef<HTMLVideoElement>(null);

  const handleMouseEnter = () => videoRef.current?.play();
  const handleMouseLeave = () => {
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  };

  return (
    <div
      className={cn('group relative overflow-hidden rounded-lg', className)}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <video
        ref={videoRef}
        className="w-full object-cover"
        poster={post.posterUrl}
        muted
        playsInline
        loop
        controls
        style={{ aspectRatio: '9/16' }}
      >
        <source src={post.videoUrl} type="video/mp4" />
      </video>
      <a
        href={post.sourceUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="bg-background/80 text-muted-foreground hover:text-foreground absolute top-2 right-2 flex size-6 items-center justify-center rounded-md transition-colors"
        onClick={(e) => e.stopPropagation()}
      >
        <ExternalLink className="size-3.5" />
      </a>
    </div>
  );
}

function VideoPostCard({ post }: { post: VideoPost }) {
  return (
    <div className="bg-card flex gap-4 rounded-xl border p-4">
      <div className="w-[160px] shrink-0">
        <PostVideo
          post={{ videoUrl: post.videoUrl, posterUrl: post.posterUrl, sourceUrl: post.sourceUrl }}
        />
      </div>
      <div className="flex min-w-0 flex-col gap-3">
        <div className="flex items-start gap-2">
          <span className="bg-primary/10 text-primary flex size-6 shrink-0 items-center justify-center rounded-full text-xs font-bold">
            {post.rank}
          </span>
          <div className="flex flex-col gap-1">
            <p className="text-foreground text-sm leading-snug font-semibold">{post.title}</p>
            <div className="flex flex-wrap gap-1.5">
              <Badge variant="secondary" className="text-xs font-normal">
                {post.gmv}
              </Badge>
              <Badge variant="secondary" className="text-xs font-normal">
                {post.creatorProfile}
              </Badge>
            </div>
          </div>
        </div>
        <p className="text-muted-foreground text-sm leading-relaxed">{post.description}</p>
        <div className="flex flex-col gap-2">
          <p className="text-foreground text-xs font-semibold">Why it performed well</p>
          <ul className="flex flex-col gap-1.5">
            {post.reasons.map((reason, i) => (
              <li key={i} className="text-muted-foreground flex gap-2 text-sm">
                <span className="text-primary mt-1 shrink-0">·</span>
                <span>{reason}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

export function AnalysisResults({ report, selectedCampaignNames, campaignName }: Props) {
  const { workspace } = useWorkspace();
  const isKo = workspace.language === 'ko';

  const [selectedIndices, setSelectedIndices] = useState<Set<number>>(new Set());
  const [videoOpenIndices, setVideoOpenIndices] = useState<Set<number>>(new Set());
  const [briefOpen, setBriefOpen] = useState(false);
  const [briefLoading, setBriefLoading] = useState(false);

  const toggleInsight = (idx: number) => {
    setSelectedIndices((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  const toggleVideo = (idx: number) => {
    setVideoOpenIndices((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  const handleOpenBrief = async () => {
    setBriefLoading(true);
    await new Promise((resolve) => setTimeout(resolve, 500));
    setBriefLoading(false);
    setBriefOpen(true);
  };

  const allInsights = report.insights;
  const doInsights = allInsights.filter((i) => i.type === 'do');
  const dontInsights = allInsights.filter((i) => i.type === 'dont');
  const selectedInsights = [...selectedIndices].sort().map((i) => allInsights[i]);
  const localizedPosts = (report.topVideoPosts ?? []).map((p) => localizePost(p, isKo));

  const campaignCount = selectedCampaignNames?.length ?? 1;
  const displayCampaignName = campaignName ?? selectedCampaignNames?.[0] ?? report.title;

  const renderInsightList = (type: 'do' | 'dont') =>
    allInsights
      .map((insight: InsightCardType, globalIdx: number) => ({ insight, globalIdx }))
      .filter(({ insight }) => insight.type === type)
      .map(({ insight, globalIdx }) => {
        const hasPosts = !!insight.referencePosts?.length;
        const videosOpen = videoOpenIndices.has(globalIdx);
        return (
          <InsightCard
            key={globalIdx}
            insight={insight}
            isSelectable
            isSelected={selectedIndices.has(globalIdx)}
            onToggle={() => toggleInsight(globalIdx)}
            action={
              hasPosts ? (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleVideo(globalIdx);
                  }}
                  className="text-muted-foreground hover:bg-muted hover:text-foreground flex items-center gap-1 rounded px-1.5 py-0.5 text-xs transition-colors"
                >
                  {videosOpen ? (
                    <>
                      <ChevronUp className="size-3" />
                      {isKo ? '영상 닫기' : 'Close videos'}
                    </>
                  ) : (
                    <>
                      <ChevronDown className="size-3" />
                      {isKo
                        ? `영상 보기 (${insight.referencePosts!.length})`
                        : `View videos (${insight.referencePosts!.length})`}
                    </>
                  )}
                </button>
              ) : undefined
            }
            expandedContent={
              hasPosts && videosOpen ? (
                <div className="flex gap-3 overflow-x-auto pt-1 pb-1">
                  {insight.referencePosts!.map((ref) => (
                    <PostVideo
                      key={ref.videoUrl}
                      post={ref}
                      className="h-[350px] w-[197px] shrink-0"
                    />
                  ))}
                </div>
              ) : undefined
            }
          />
        );
      });

  return (
    <div className="relative flex gap-6 pb-24">
      {/* Left: insights + top video posts */}
      <div className="flex min-w-0 flex-1 flex-col gap-8">
        {/* Total insights */}
        <section className="flex flex-col gap-4">
          <div className="flex items-end justify-between gap-3">
            <div className="flex flex-col gap-1">
              <h2 className="text-foreground text-base font-semibold">Total insights</h2>
              <p className="text-muted-foreground text-sm">
                {isKo
                  ? '카드를 선택해 Content Brief를 생성하세요'
                  : 'Select cards to generate a Content Brief'}
              </p>
            </div>
            {selectedIndices.size > 0 && (
              <span className="text-muted-foreground shrink-0 text-xs">
                {isKo ? `${selectedIndices.size}개 선택됨` : `${selectedIndices.size} selected`}
              </span>
            )}
          </div>

          {doInsights.length > 0 && (
            <div className="flex flex-col gap-3">
              <p className="text-xs font-semibold tracking-wider text-emerald-600 uppercase">
                What Works ({doInsights.length})
              </p>
              <div className="flex flex-col gap-3">{renderInsightList('do')}</div>
            </div>
          )}

          {dontInsights.length > 0 && (
            <div className="flex flex-col gap-3">
              <p className="text-xs font-semibold tracking-wider text-rose-600 uppercase">
                What Doesn't Work ({dontInsights.length})
              </p>
              <div className="flex flex-col gap-3">{renderInsightList('dont')}</div>
            </div>
          )}
        </section>

        {/* Top 5 video insights */}
        {localizedPosts.length > 0 && (
          <section className="flex flex-col gap-4">
            <div className="flex items-center gap-2">
              <Trophy className="size-4 text-amber-500" />
              <h2 className="text-foreground text-base font-semibold">Top 5 video insights</h2>
            </div>
            <p className="text-muted-foreground -mt-2 text-sm">
              Top 5 posts by Engagement score. Review the common success patterns observed across
              each video.
            </p>
            <div className="flex flex-col gap-4">
              {localizedPosts.map((post) => (
                <VideoPostCard key={post.rank} post={post} />
              ))}
            </div>
          </section>
        )}
      </div>

      {/* Right: sticky sidebar */}
      <div className="w-64 shrink-0">
        <div className="bg-card sticky top-4 flex flex-col gap-4 rounded-xl border p-5">
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between text-xs">
              <span className="text-muted-foreground">{isKo ? '캠페인' : 'Campaigns'}</span>
              <span className="font-medium">{campaignCount}</span>
            </div>
            <div className="flex items-center justify-between text-xs">
              <span className="text-muted-foreground">
                {isKo ? '분석 포스트' : 'Posts analyzed'}
              </span>
              <span className="font-medium">{report.postsAnalyzed.toLocaleString()}</span>
            </div>
          </div>
          <div className="flex flex-col gap-1.5 border-t pt-3">
            <p className="text-muted-foreground text-xs">
              {isKo ? '입력 조건' : 'Input conditions'}
            </p>
            <div className="flex flex-wrap gap-1.5">
              {displayCampaignName && (
                <Badge variant="secondary" className="text-xs font-normal">
                  {displayCampaignName}
                </Badge>
              )}
              <Badge variant="secondary" className="text-xs font-normal">
                {report.platform}
              </Badge>
              <Badge variant="secondary" className="text-xs font-normal">
                {report.metric}
              </Badge>
            </div>
          </div>
        </div>
      </div>

      {/* Floating Content Brief button */}
      {selectedIndices.size > 0 && (
        <div className="fixed bottom-6 left-1/2 z-50 -translate-x-1/2">
          <Button
            onClick={handleOpenBrief}
            disabled={briefLoading}
            className="shadow-primary/20 gap-2 px-5 shadow-lg"
            size="lg"
          >
            {briefLoading ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <FileText className="size-4" />
            )}
            {briefLoading
              ? isKo
                ? '생성 중...'
                : 'Generating...'
              : isKo
                ? `Content Brief 생성 (${selectedIndices.size}개)`
                : `Generate Content Brief (${selectedIndices.size})`}
          </Button>
        </div>
      )}

      <PlanningBriefSheet
        open={briefOpen}
        onOpenChange={setBriefOpen}
        insights={selectedInsights}
        brandInfo={null}
        creatorGuidePrompts={report.creatorGuidePrompts ?? []}
      />
    </div>
  );
}
